import { Controller } from "@hotwired/stimulus";
import { closeDots } from "../lib/dot_peen";

const NO_DECODE_DELAY = 8000;
const LOCK_HOLD = 500;
// The dot-peen retry targets laser-etched matrix codes, so it re-reads for
// matrix formats only. Re-running every 1D format over a binarised image would
// widen the false-positive surface for no gain — the linear codes in the corpus
// all decode on the first pass.
const DOT_PEEN_FORMATS = ["DataMatrix", "QRCode"];

// Frame D (live camera) and frame E (photo fallback). zxing-wasm is
// dynamically imported so its wasm loader only ships to pages that render
// this controller, and its .wasm binary is served from our own build (a
// Vite `?url` import), not the package's default CDN fetch.
export default class extends Controller {
  static targets = ["video", "canvas", "status", "label", "viewport", "raw", "form", "fileInput", "photoFailure"];
  static values = {
    formats: Array,
    extendedFormats: Array,
    interval: { type: Number, default: 120 },
    pointAtBarcode: String,
    noDecodeHint: String,
    cameraUnavailable: String,
    noCodeFound: String,
    photoChecking: String,
    photoCheckedLabel: String,
  };

  connect() {
    // Turbo paints a cached preview of this page from the last visit while
    // the real navigation loads behind it. That preview DOM is torn down
    // the instant the real page arrives, so starting the camera on it would
    // double-mount a stream that's immediately orphaned — the tab's camera
    // indicator stays lit with nothing left to stop it. Never start on a
    // preview render; the real connect() fires right after.
    if (document.documentElement.hasAttribute("data-turbo-preview")) return;

    this.connected = true;
    this.stream = null;
    this.loopTimer = null;
    this.noDecodeTimer = null;
    this.lockTimer = null;
    this.readBarcodes = null;
    this.canvasContext = this.canvasTarget.getContext("2d", { willReadFrequently: true });
    this.startCamera();
  }

  disconnect() {
    this.connected = false;
    this.stopLiveLoop();
    clearTimeout(this.lockTimer);
    this.stopCamera();
  }

  async startCamera() {
    if (!navigator.mediaDevices?.getUserMedia) return this.showUnavailable();

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 } },
      });
    } catch {
      return this.showUnavailable();
    }
    // disconnected while awaiting permission — stopCamera also covers the
    // video target being gone, so the stream we just opened doesn't leak.
    if (!this.connected) return this.stopCamera();

    this.videoTarget.srcObject = this.stream;
    await this.videoTarget.play().catch(() => {});

    try {
      await this.loadReader();
    } catch {
      return this.showUnavailable();
    }
    if (!this.connected) return this.stopCamera(); // disconnected while awaiting the reader

    this.statusTarget.textContent = this.pointAtBarcodeValue;
    this.armNoDecodeTimer();
    this.scheduleFrame();
  }

  async loadReader() {
    if (this.readBarcodes) return;

    const [{ readBarcodes, prepareZXingModule }, { default: wasmUrl }] = await Promise.all([
      import("zxing-wasm/reader"),
      import("zxing-wasm/reader/zxing_reader.wasm?url"),
    ]);
    prepareZXingModule({ overrides: { locateFile: (path, prefix) => (path.endsWith(".wasm") ? wasmUrl : prefix + path) } });
    this.readBarcodes = readBarcodes;
  }

  scheduleFrame() {
    this.loopTimer = setTimeout(() => this.captureFrame(), this.intervalValue);
  }

  async captureFrame() {
    const video = this.videoTarget;
    if (!video.videoWidth) return this.scheduleFrame();

    const canvas = this.canvasTarget;
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
    }
    this.canvasContext.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = this.canvasContext.getImageData(0, 0, canvas.width, canvas.height);

    let results = [];
    try {
      results = await this.readBarcodes(imageData, { formats: this.formatsValue, tryHarder: false });
    } catch {
      results = [];
    }
    if (!this.connected) return this.stopCamera(); // disconnected mid-decode

    if (results[0]?.text) {
      this.lockOn(results[0].text);
    } else {
      this.scheduleFrame();
    }
  }

  // Brackets tighten, the line settles, a tick holds ~500ms (spec §12)
  // before the confirm GET fires.
  lockOn(text) {
    this.stopLiveLoop();
    this.viewportTarget.classList.add("lock");
    this.rawTarget.value = text;
    this.lockTimer = setTimeout(() => this.formTarget.requestSubmit(), LOCK_HOLD);
  }

  armNoDecodeTimer() {
    this.noDecodeTimer = setTimeout(() => {
      this.statusTarget.textContent = this.noDecodeHintValue;
    }, NO_DECODE_DELAY);
  }

  showUnavailable() {
    this.stopLiveLoop();
    if (!this.hasViewportTarget) return;

    this.viewportTarget.classList.add("unavailable");
    this.statusTarget.textContent = this.cameraUnavailableValue;
  }

  openCamera() {
    this.fileInputTarget.value = "";
    this.fileInputTarget.click();
  }

  async decodePhoto() {
    const file = this.fileInputTarget.files[0];
    if (!file) return;

    this.stopLiveLoop();
    this.stopCamera(); // frame E: the camera is not running while a still is decoded
    this.statusTarget.textContent = this.photoCheckingValue;

    try {
      await this.loadReader();
    } catch {
      return this.showPhotoFailure();
    }
    if (!this.connected) return this.stopCamera(); // disconnected while awaiting the reader

    const formats = [...this.formatsValue, ...this.extendedFormatsValue];
    let results = [];
    try {
      results = await this.readBarcodes(file, { formats, tryHarder: true });
    } catch {
      results = [];
    }
    if (!this.connected) return this.stopCamera(); // disconnected mid-decode

    // Only once an ordinary read has come up empty: a dot-peen carton costs a
    // full-frame binarise + close, which is not worth spending on a photo the
    // reader could already handle.
    if (!results[0]?.text) {
      try {
        results = await this.decodeDotPeen(file);
      } catch {
        results = [];
      }
      if (!this.connected) return this.stopCamera(); // disconnected mid-retry
    }

    if (results[0]?.text) {
      this.rawTarget.value = results[0].text;
      this.formTarget.requestSubmit();
    } else {
      this.showPhotoFailure();
    }
  }

  // Re-read the still through a morphological close, which merges a dot-peen
  // matrix code's separated dots back into solid modules. Runs at the photo's
  // native resolution — upscaling first was measured as no more effective and
  // costs four times the pixels.
  async decodeDotPeen(file) {
    const bitmap = await createImageBitmap(file);
    const canvas = this.canvasTarget;
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    this.canvasContext.drawImage(bitmap, 0, 0);
    bitmap.close(); // frees the decoded frame before we hold a second copy as ImageData

    const imageData = this.canvasContext.getImageData(0, 0, canvas.width, canvas.height);
    return this.readBarcodes(closeDots(imageData), { formats: DOT_PEEN_FORMATS, tryHarder: true });
  }

  showPhotoFailure() {
    if (!this.hasViewportTarget) return;

    this.labelTarget.textContent = this.photoCheckedLabelValue;
    this.statusTarget.textContent = this.noCodeFoundValue;
    this.viewportTarget.classList.add("photo-failed");
    // Two elements share this target: the in-card hints and the PhoneFoot
    // actions (Take another photo / Search instead) rendered as a sibling
    // of the card — both reveal together.
    this.photoFailureTargets.forEach((target) => target.classList.remove("hidden"));
  }

  stopLiveLoop() {
    clearTimeout(this.loopTimer);
    clearTimeout(this.noDecodeTimer);
    this.loopTimer = null;
    this.noDecodeTimer = null;
  }

  stopCamera() {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
  }
}
